import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

// Full-bleed band with a scroll-driven parallax background image.
export const ParallaxBand = ({ image, overlay = "bg-header/72", children, className = "" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  return (
    <section ref={ref} className={`relative overflow-hidden ${className}`}>
      <motion.img
        style={{ y }} src={image} alt="" aria-hidden="true"
        className="absolute -top-[12%] left-0 h-[124%] w-full object-cover"
      />
      <div className={`absolute inset-0 ${overlay}`} />
      <div className="relative">{children}</div>
    </section>
  );
};

// Reveal-on-scroll wrapper. Animates transform + opacity only (perf-safe).
export const Reveal = ({ children, delay = 0, y = 16, className, as = "div" }) => {
  const M = motion[as] || motion.div;
  return (
    <M
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </M>
  );
};

// Staggered container for groups of revealing children.
export const RevealGroup = ({ children, className, stagger = 0.08 }) => (
  <motion.div
    className={className}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-10% 0px -10% 0px" }}
    variants={{ hidden: {}, show: { transition: { staggerChildren: stagger } } }}
  >
    {children}
  </motion.div>
);

export const RevealItem = ({ children, className, y = 16 }) => (
  <motion.div
    className={className}
    variants={{
      hidden: { opacity: 0, y },
      show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
    }}
  >
    {children}
  </motion.div>
);
